import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Plus } from 'lucide-react';
import { ewmAPI, materialManagementAPI, masterDataAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';

const EWM = () => {
  const { canWrite } = useAuth();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'warehouses');
  const [warehouses, setWarehouses] = useState([]);
  const [storageTypes, setStorageTypes] = useState([]);
  const [storageSections, setStorageSections] = useState([]);
  const [storageBins, setStorageBins] = useState([]);
  const [countries, setCountries] = useState([]);
  const [factoryCalendars, setFactoryCalendars] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [viewItem, setViewItem] = useState(null);
  const [formResetCounter, setFormResetCounter] = useState(0);
  
  // Form state
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedFactoryCalendar, setSelectedFactoryCalendar] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('Active');
  const [selectedWarehouseType, setSelectedWarehouseType] = useState('');
  const [selectedStorageTypeClass, setSelectedStorageTypeClass] = useState('');
  const [selectedPutawayStrategy, setSelectedPutawayStrategy] = useState('');
  const [selectedRemovalStrategy, setSelectedRemovalStrategy] = useState('');
  const [selectedCapacityCheck, setSelectedCapacityCheck] = useState('');
  const [mixedStorageAllowed, setMixedStorageAllowed] = useState(false);
  const [sectionIndicator, setSectionIndicator] = useState(false);
  const [hazardousAllowed, setHazardousAllowed] = useState(false);
  const [temperatureControlled, setTemperatureControlled] = useState(false);
  const [selectedSectionType, setSelectedSectionType] = useState('');
  const [pickingSequence, setPickingSequence] = useState('');
  const [selectedBinType, setSelectedBinType] = useState('');
  const [capacityQty, setCapacityQty] = useState('');
  const [capacityUnit, setCapacityUnit] = useState('');
  const [tempMin, setTempMin] = useState('');
  const [tempMax, setTempMax] = useState('');
  const [blockedPutaway, setBlockedPutaway] = useState(false);
  const [blockedPicking, setBlockedPicking] = useState(false);

  const tabs = [
    { id: 'warehouses', label: 'Warehouses' },
    { id: 'storage-types', label: 'Storage Types' },
    { id: 'storage-sections', label: 'Storage Sections' },
    { id: 'storage-bins', label: 'Storage Bins' },
  ];

  useEffect(() => {
    loadData();
    loadMasterData();
  }, []);

  // Reset form state when tab changes
  useEffect(() => {
    setEditingItem(null);
    setIsDialogOpen(false);
    setSelectedCountry('');
    setSelectedFactoryCalendar('');
    setSelectedStatus('Active');
    setSelectedWarehouseType('');
    setSelectedStorageTypeClass('');
    setSelectedPutawayStrategy('');
    setSelectedRemovalStrategy('');
    setSelectedCapacityCheck('');
    setMixedStorageAllowed(false);
    setSectionIndicator(false);
    setHazardousAllowed(false);
    setTemperatureControlled(false);
    setSelectedSectionType('');
    setPickingSequence('');
    setSelectedBinType('');
    setCapacityQty('');
    setCapacityUnit('');
    setTempMin('');
    setTempMax('');
    setBlockedPutaway(false);
    setBlockedPicking(false);
    setFormResetCounter(prev => prev + 1);
  }, [activeTab]);

  const loadMasterData = async () => {
    try {
      const [countriesRes, factoryCalsRes] = await Promise.all([
        masterDataAPI.getCountries(),
        materialManagementAPI.getFactoryCalendars(),
      ]);
      setCountries(countriesRes.data);
      setFactoryCalendars(factoryCalsRes.data);
    } catch (error) {
      console.error('Failed to load master data:', error);
      toast.error('Failed to load master data');
    }
  };

  const loadData = async () => {
    try {
      const [warehousesRes, storageTypesRes, storageSectionsRes, storageBinsRes] = await Promise.all([
        ewmAPI.getWarehouses(),
        ewmAPI.getStorageTypes(),
        ewmAPI.getStorageSections(),
        ewmAPI.getStorageBins(),
      ]);
      setWarehouses(warehousesRes.data);
      setStorageTypes(storageTypesRes.data);
      setStorageSections(storageSectionsRes.data);
      setStorageBins(storageBinsRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load data');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const formValues = Object.fromEntries(formData.entries());

    try {
      if (activeTab === 'warehouses') {
        const payload = {
          warehouse_number: formValues.warehouse_number,
          name: formValues.name,
          description: formValues.description || null,
          plant_id: null,
          address: formValues.address || null,
          region: formValues.region || null,
          country: formValues.country,
          status: formValues.status,
          warehouse_type: formValues.warehouse_type || null,
          time_zone: formValues.time_zone || null,
          factory_calendar_id: formValues.factory_calendar_id,
          opening_date: formValues.opening_date || null,
          closing_date: formValues.closing_date || null,
          manager_id: formValues.manager_id || null,
          erp_warehouse_id: formValues.erp_warehouse_id || null,
        };

        if (editingItem) {
          await ewmAPI.updateWarehouse(editingItem.warehouse_number, payload);
          toast.success('Warehouse updated successfully');
        } else {
          await ewmAPI.createWarehouse(payload);
          toast.success('Warehouse created successfully');
        }
      } else if (activeTab === 'storage-types') {
        const payload = {
          storage_type_id: formValues.storage_type_id,
          warehouse_number: null, // Warehouse assignment done via separate assignment entity
          name: formValues.name,
          description: formValues.description || null,
          storage_type_class: formValues.storage_type_class,
          putaway_strategy: formValues.putaway_strategy,
          removal_strategy: formValues.removal_strategy,
          mixed_storage_allowed: formValues.mixed_storage_allowed === 'true',
          section_indicator: formValues.section_indicator === 'true',
          capacity_check_method: formValues.capacity_check_method || null,
          hazardous_allowed: formValues.hazardous_allowed === 'true',
          temperature_controlled: formValues.temperature_controlled === 'true',
          status: formValues.status,
        };

        if (editingItem) {
          await ewmAPI.updateStorageType(
            editingItem.storage_type_id,
            payload
          );
          toast.success('Storage Type updated successfully');
        } else {
          await ewmAPI.createStorageType(payload);
          toast.success('Storage Type created successfully');
        }
      } else if (activeTab === 'storage-sections') {
        const payload = {
          storage_section_id: formValues.storage_section_id,
          storage_type_id: null, // Storage Type assignment done via separate assignment entity
          name: formValues.name,
          description: formValues.description || null,
          section_type: formValues.section_type || null,
          picking_sequence: formValues.picking_sequence ? parseInt(formValues.picking_sequence) : null,
          special_handling: formValues.special_handling || null,
          status: formValues.status,
        };

        if (editingItem) {
          await ewmAPI.updateStorageSection(
            editingItem.storage_section_id,
            payload
          );
          toast.success('Storage Section updated successfully');
        } else {
          await ewmAPI.createStorageSection(payload);
          toast.success('Storage Section created successfully');
        }
      } else if (activeTab === 'storage-bins') {
        const payload = {
          storage_bin_id: formValues.storage_bin_id,
          storage_section_id: null, // Storage Section assignment done via separate assignment entity
          name: formValues.name || null,
          description: formValues.description || null,
          bin_type: formValues.bin_type || null,
          capacity_qty: formValues.capacity_qty ? parseFloat(formValues.capacity_qty) : null,
          capacity_unit: formValues.capacity_unit || null,
          hazardous_allowed: formValues.hazardous_allowed === 'true',
          temperature_min: formValues.temperature_min ? parseFloat(formValues.temperature_min) : null,
          temperature_max: formValues.temperature_max ? parseFloat(formValues.temperature_max) : null,
          blocked_for_putaway: formValues.blocked_for_putaway === 'true',
          blocked_for_picking: formValues.blocked_for_picking === 'true',
          status: formValues.status,
          picking_sequence: formValues.picking_sequence ? parseInt(formValues.picking_sequence) : null,
        };

        if (editingItem) {
          await ewmAPI.updateStorageBin(
            editingItem.storage_bin_id,
            payload
          );
          toast.success('Storage Bin updated successfully');
        } else {
          await ewmAPI.createStorageBin(payload);
          toast.success('Storage Bin created successfully');
        }
      }

      setIsDialogOpen(false);
      setEditingItem(null);
      setSelectedCountry('');
      setSelectedFactoryCalendar('');
      setSelectedStatus('Active');
      setSelectedWarehouseType('');
      setSelectedStorageTypeClass('');
      setSelectedPutawayStrategy('');
      setSelectedRemovalStrategy('');
      setSelectedCapacityCheck('');
      setMixedStorageAllowed(false);
      setSectionIndicator(false);
      setHazardousAllowed(false);
      setTemperatureControlled(false);
      setSelectedSectionType('');
      setPickingSequence('');
      setSelectedBinType('');
      setCapacityQty('');
      setCapacityUnit('');
      setTempMin('');
      setTempMax('');
      setBlockedPutaway(false);
      setBlockedPicking(false);
      setFormResetCounter(prev => prev + 1);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    
    if (activeTab === 'warehouses') {
      setSelectedCountry(item.country);
      setSelectedFactoryCalendar(item.factory_calendar_id);
      setSelectedStatus(item.status);
      setSelectedWarehouseType(item.warehouse_type || '');
    } else if (activeTab === 'storage-types') {
      setSelectedStorageTypeClass(item.storage_type_class);
      setSelectedPutawayStrategy(item.putaway_strategy);
      setSelectedRemovalStrategy(item.removal_strategy);
      setSelectedCapacityCheck(item.capacity_check_method || '');
      setSelectedStatus(item.status);
      setMixedStorageAllowed(item.mixed_storage_allowed);
      setSectionIndicator(item.section_indicator);
      setHazardousAllowed(item.hazardous_allowed);
      setTemperatureControlled(item.temperature_controlled);
    } else if (activeTab === 'storage-sections') {
      setSelectedSectionType(item.section_type || '');
      setPickingSequence(item.picking_sequence || '');
      setSelectedStatus(item.status);
    } else if (activeTab === 'storage-bins') {
      setSelectedBinType(item.bin_type || '');
      setCapacityQty(item.capacity_qty || '');
      setCapacityUnit(item.capacity_unit || '');
      setTempMin(item.temperature_min || '');
      setTempMax(item.temperature_max || '');
      setBlockedPutaway(item.blocked_for_putaway);
      setBlockedPicking(item.blocked_for_picking);
      setHazardousAllowed(item.hazardous_allowed);
      setPickingSequence(item.picking_sequence || '');
      setSelectedStatus(item.status);
    }
    
    setIsDialogOpen(true);
  };

  const handleDelete = async (item) => {
    const entityMap = {
      'warehouses': 'warehouse',
      'storage-types': 'storage type',
      'storage-sections': 'storage section',
      'storage-bins': 'storage bin'
    };
    const entityName = entityMap[activeTab];
    if (!window.confirm(`Are you sure you want to delete this ${entityName}?`)) return;

    try {
      if (activeTab === 'warehouses') {
        await ewmAPI.deleteWarehouse(item.warehouse_number);
        toast.success('Warehouse deleted successfully');
      } else if (activeTab === 'storage-types') {
        await ewmAPI.deleteStorageType(item.storage_type_id);
        toast.success('Storage Type deleted successfully');
      } else if (activeTab === 'storage-sections') {
        await ewmAPI.deleteStorageSection(item.storage_section_id);
        toast.success('Storage Section deleted successfully');
      } else if (activeTab === 'storage-bins') {
        await ewmAPI.deleteStorageBin(item.storage_bin_id);
        toast.success('Storage Bin deleted successfully');
      }
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  const getCurrentData = () => {
    switch (activeTab) {
      case 'warehouses':
        return warehouses;
      case 'storage-types':
        return storageTypes;
      case 'storage-sections':
        return storageSections;
      case 'storage-bins':
        return storageBins;
      default:
        return [];
    }
  };

  const getTableColumns = () => {
    switch (activeTab) {
      case 'warehouses':
        return ['Warehouse Number', 'Name', 'Country', 'Status', 'Warehouse Type', 'Factory Calendar', 'Actions'];
      case 'storage-types':
        return ['Storage Type ID', 'Name', 'Class', 'Putaway Strategy', 'Removal Strategy', 'Status', 'Actions'];
      case 'storage-sections':
        return ['Section ID', 'Name', 'Section Type', 'Picking Sequence', 'Status', 'Actions'];
      case 'storage-bins':
        return ['Bin ID', 'Name', 'Bin Type', 'Capacity', 'Blocked', 'Status', 'Actions'];
      default:
        return [];
    }
  };

  const getTableRow = (item) => {
    if (activeTab === 'warehouses') {
      return (
        <>
          <td className="py-3 px-4 font-mono text-sm font-semibold">{item.warehouse_number}</td>
          <td className="py-3 px-4 text-sm">{item.name}</td>
          <td className="py-3 px-4 font-mono text-xs">{item.country}</td>
          <td className="py-3 px-4 text-xs">
            <span className={`px-2 py-1 rounded ${
              item.status === 'Active' ? 'bg-green-100 text-green-700' : 
              item.status === 'Inactive' ? 'bg-yellow-100 text-yellow-700' : 
              'bg-red-100 text-red-700'
            }`}>
              {item.status}
            </span>
          </td>
          <td className="py-3 px-4 text-xs">{item.warehouse_type || '-'}</td>
          <td className="py-3 px-4 font-mono text-xs">{item.factory_calendar_id}</td>
        </>
      );
    } else if (activeTab === 'storage-types') {
      return (
        <>
          <td className="py-3 px-4 font-mono text-sm font-semibold">{item.storage_type_id}</td>
          <td className="py-3 px-4 text-sm">{item.name}</td>
          <td className="py-3 px-4 text-xs">{item.storage_type_class}</td>
          <td className="py-3 px-4 text-xs">{item.putaway_strategy}</td>
          <td className="py-3 px-4 text-xs">{item.removal_strategy}</td>
          <td className="py-3 px-4 text-xs">
            <span className={`px-2 py-1 rounded ${
              item.status === 'Active' ? 'bg-green-100 text-green-700' : 
              item.status === 'Inactive' ? 'bg-yellow-100 text-yellow-700' : 
              'bg-red-100 text-red-700'
            }`}>
              {item.status}
            </span>
          </td>
        </>
      );
    } else if (activeTab === 'storage-sections') {
      return (
        <>
          <td className="py-3 px-4 font-mono text-sm font-semibold">{item.storage_section_id}</td>
          <td className="py-3 px-4 text-sm">{item.name}</td>
          <td className="py-3 px-4 text-xs">{item.section_type || '-'}</td>
          <td className="py-3 px-4 text-xs text-center">{item.picking_sequence || '-'}</td>
          <td className="py-3 px-4 text-xs">
            <span className={`px-2 py-1 rounded ${
              item.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}>
              {item.status}
            </span>
          </td>
        </>
      );
    } else if (activeTab === 'storage-bins') {
      const blockedStatus = [];
      if (item.blocked_for_putaway) blockedStatus.push('Putaway');
      if (item.blocked_for_picking) blockedStatus.push('Picking');
      const blockedText = blockedStatus.length > 0 ? blockedStatus.join(', ') : '-';
      
      return (
        <>
          <td className="py-3 px-4 font-mono text-sm font-semibold">{item.storage_bin_id}</td>
          <td className="py-3 px-4 text-sm">{item.name || '-'}</td>
          <td className="py-3 px-4 text-xs">{item.bin_type || '-'}</td>
          <td className="py-3 px-4 text-xs">
            {item.capacity_qty ? `${item.capacity_qty} ${item.capacity_unit || ''}` : '-'}
          </td>
          <td className="py-3 px-4 text-xs">{blockedText}</td>
          <td className="py-3 px-4 text-xs">
            <span className={`px-2 py-1 rounded ${
              item.status === 'Active' ? 'bg-green-100 text-green-700' : 
              item.status === 'Inactive' ? 'bg-yellow-100 text-yellow-700' : 
              'bg-red-100 text-red-700'
            }`}>
              {item.status}
            </span>
          </td>
        </>
      );
    }
    return null;
  };

  const getFormFields = () => {
    if (activeTab === 'warehouses') {
      return (
        <>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="warehouse_number">Warehouse Number *</Label>
            <Input 
              name="warehouse_number" 
              defaultValue={editingItem?.warehouse_number}
              required 
              disabled={!!editingItem}
              placeholder="e.g., 001" 
              maxLength={10}
            />
          </div>
          <div>
            <Label htmlFor="name">Name *</Label>
            <Input 
              name="name" 
              defaultValue={editingItem?.name}
              required 
              placeholder="e.g., Central Distribution Warehouse" 
            />
          </div>
        </div>

        <div>
          <Label htmlFor="description">Description</Label>
          <Input 
            name="description" 
            defaultValue={editingItem?.description}
            placeholder="Long-form name/details" 
          />
        </div>

        <div>
          <Label htmlFor="country">Country *</Label>
          <Select value={selectedCountry} onValueChange={setSelectedCountry} required>
            <SelectTrigger>
              <SelectValue placeholder="Select Country" />
            </SelectTrigger>
            <SelectContent>
              {countries.map(country => (
                <SelectItem key={country.country_code} value={country.country_code}>
                  {country.country_code} - {country.country_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <input type="hidden" name="country" value={selectedCountry} />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="address">Address</Label>
            <Input 
              name="address" 
              defaultValue={editingItem?.address}
              placeholder="Physical address/warehouse site" 
            />
          </div>
          <div>
            <Label htmlFor="region">Region</Label>
            <Input 
              name="region" 
              defaultValue={editingItem?.region}
              placeholder="e.g., TN, Karnataka" 
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="status">Status *</Label>
            <Select value={selectedStatus} onValueChange={setSelectedStatus} required>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Inactive">Inactive</SelectItem>
                <SelectItem value="Blocked">Blocked</SelectItem>
              </SelectContent>
            </Select>
            <input type="hidden" name="status" value={selectedStatus} />
          </div>

          <div>
            <Label htmlFor="warehouse_type">Warehouse Type</Label>
            <Select value={selectedWarehouseType || undefined} onValueChange={setSelectedWarehouseType}>
              <SelectTrigger>
                <SelectValue placeholder="Select Type (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Distribution Center">Distribution Center</SelectItem>
                <SelectItem value="Production Warehouse">Production Warehouse</SelectItem>
                <SelectItem value="Transit Warehouse">Transit Warehouse</SelectItem>
                <SelectItem value="Returns Center">Returns Center</SelectItem>
                <SelectItem value="Cross Dock">Cross Dock</SelectItem>
                <SelectItem value="Third Party Warehouse">Third Party Warehouse</SelectItem>
              </SelectContent>
            </Select>
            <input type="hidden" name="warehouse_type" value={selectedWarehouseType || ''} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="factory_calendar_id">Factory Calendar *</Label>
            <Select value={selectedFactoryCalendar} onValueChange={setSelectedFactoryCalendar} required>
              <SelectTrigger>
                <SelectValue placeholder="Select Factory Calendar" />
              </SelectTrigger>
              <SelectContent>
                {factoryCalendars.map(fc => (
                  <SelectItem key={fc.factory_calendar_id} value={fc.factory_calendar_id}>
                    {fc.factory_calendar_id} - {fc.description}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input type="hidden" name="factory_calendar_id" value={selectedFactoryCalendar} />
          </div>

          <div>
            <Label htmlFor="time_zone">Time Zone</Label>
            <Input 
              name="time_zone" 
              defaultValue={editingItem?.time_zone}
              placeholder="e.g., Asia/Kolkata" 
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="opening_date">Opening Date</Label>
            <Input 
              name="opening_date" 
              type="date"
              defaultValue={editingItem?.opening_date}
            />
          </div>
          <div>
            <Label htmlFor="closing_date">Closing Date</Label>
            <Input 
              name="closing_date" 
              type="date"
              defaultValue={editingItem?.closing_date}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="manager_id">Manager ID</Label>
            <Input 
              name="manager_id" 
              defaultValue={editingItem?.manager_id}
              placeholder="Responsible person/main contact" 
            />
          </div>
          <div>
            <Label htmlFor="erp_warehouse_id">ERP Warehouse ID</Label>
            <Input 
              name="erp_warehouse_id" 
              defaultValue={editingItem?.erp_warehouse_id}
              placeholder="Link to legacy/MM warehouse" 
            />
          </div>
        </div>
      </>
      );
    } else if (activeTab === 'storage-types') {
      return (
        <>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="storage_type_id">Storage Type ID *</Label>
              <Input 
                name="storage_type_id" 
                defaultValue={editingItem?.storage_type_id}
                required 
                disabled={!!editingItem}
                placeholder="e.g., SR1, 001" 
                maxLength={10}
              />
            </div>
            <div>
              <Label htmlFor="name">Name *</Label>
              <Input 
                name="name" 
                defaultValue={editingItem?.name}
                required 
                placeholder="e.g., High Rack - Pallet Storage" 
              />
            </div>
            <div>
              <Label htmlFor="storage_type_class">Storage Type Class *</Label>
              <Select value={selectedStorageTypeClass} onValueChange={setSelectedStorageTypeClass} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select Class" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="High Rack">High Rack</SelectItem>
                  <SelectItem value="Shelf">Shelf</SelectItem>
                  <SelectItem value="Bulk">Bulk</SelectItem>
                  <SelectItem value="Goods Receipt">Goods Receipt</SelectItem>
                  <SelectItem value="Staging">Staging</SelectItem>
                  <SelectItem value="PickFace">PickFace</SelectItem>
                  <SelectItem value="Open Storage">Open Storage</SelectItem>
                  <SelectItem value="Cross Dock">Cross Dock</SelectItem>
                </SelectContent>
              </Select>
              <input type="hidden" name="storage_type_class" value={selectedStorageTypeClass} />
            </div>
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Input 
              name="description" 
              defaultValue={editingItem?.description}
              placeholder="Long-form description or special notes" 
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="putaway_strategy">Putaway Strategy *</Label>
              <Select value={selectedPutawayStrategy} onValueChange={setSelectedPutawayStrategy} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select Strategy" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Next Empty Bin">Next Empty Bin</SelectItem>
                  <SelectItem value="Fixed Bin">Fixed Bin</SelectItem>
                  <SelectItem value="Addition to Stock">Addition to Stock</SelectItem>
                  <SelectItem value="Open Storage">Open Storage</SelectItem>
                  <SelectItem value="Closest to Goods Receipt">Closest to Goods Receipt</SelectItem>
                  <SelectItem value="Bulk Storage">Bulk Storage</SelectItem>
                </SelectContent>
              </Select>
              <input type="hidden" name="putaway_strategy" value={selectedPutawayStrategy} />
            </div>
            <div>
              <Label htmlFor="removal_strategy">Removal Strategy *</Label>
              <Select value={selectedRemovalStrategy} onValueChange={setSelectedRemovalStrategy} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select Strategy" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="FIFO">FIFO</SelectItem>
                  <SelectItem value="LIFO">LIFO</SelectItem>
                  <SelectItem value="FEFO">FEFO</SelectItem>
                  <SelectItem value="Addition to Stock">Addition to Stock</SelectItem>
                  <SelectItem value="Partial Quantity">Partial Quantity</SelectItem>
                  <SelectItem value="Empty Storage Type">Empty Storage Type</SelectItem>
                </SelectContent>
              </Select>
              <input type="hidden" name="removal_strategy" value={selectedRemovalStrategy} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="status">Status *</Label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus} required>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Inactive">Inactive</SelectItem>
                  <SelectItem value="Blocked">Blocked</SelectItem>
                </SelectContent>
              </Select>
              <input type="hidden" name="status" value={selectedStatus} />
            </div>
            <div>
              <Label htmlFor="capacity_check_method">Capacity Check Method</Label>
              <Select value={selectedCapacityCheck || undefined} onValueChange={setSelectedCapacityCheck}>
                <SelectTrigger>
                  <SelectValue placeholder="Select Method (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="By Bin">By Bin</SelectItem>
                  <SelectItem value="By Weight">By Weight</SelectItem>
                  <SelectItem value="By Volume">By Volume</SelectItem>
                  <SelectItem value="None">None</SelectItem>
                </SelectContent>
              </Select>
              <input type="hidden" name="capacity_check_method" value={selectedCapacityCheck || ''} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  id="mixed_storage_allowed"
                  name="mixed_storage_allowed_display"
                  checked={mixedStorageAllowed}
                  onChange={(e) => setMixedStorageAllowed(e.target.checked)}
                  className="w-4 h-4"
                />
                <Label htmlFor="mixed_storage_allowed" className="cursor-pointer">
                  Mixed Storage Allowed
                </Label>
              </div>
              <input type="hidden" name="mixed_storage_allowed" value={mixedStorageAllowed.toString()} />
              
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  id="section_indicator"
                  name="section_indicator_display"
                  checked={sectionIndicator}
                  onChange={(e) => setSectionIndicator(e.target.checked)}
                  className="w-4 h-4"
                />
                <Label htmlFor="section_indicator" className="cursor-pointer">
                  Section Indicator
                </Label>
              </div>
              <input type="hidden" name="section_indicator" value={sectionIndicator.toString()} />
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  id="hazardous_allowed"
                  name="hazardous_allowed_display"
                  checked={hazardousAllowed}
                  onChange={(e) => setHazardousAllowed(e.target.checked)}
                  className="w-4 h-4"
                />
                <Label htmlFor="hazardous_allowed" className="cursor-pointer">
                  Hazardous Materials Allowed
                </Label>
              </div>
              <input type="hidden" name="hazardous_allowed" value={hazardousAllowed.toString()} />
              
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  id="temperature_controlled"
                  name="temperature_controlled_display"
                  checked={temperatureControlled}
                  onChange={(e) => setTemperatureControlled(e.target.checked)}
                  className="w-4 h-4"
                />
                <Label htmlFor="temperature_controlled" className="cursor-pointer">
                  Temperature Controlled
                </Label>
              </div>
              <input type="hidden" name="temperature_controlled" value={temperatureControlled.toString()} />
            </div>
          </div>
        </>
      );
    } else if (activeTab === 'storage-sections') {
      return (
        <>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="storage_section_id">Storage Section ID *</Label>
              <Input 
                name="storage_section_id" 
                defaultValue={editingItem?.storage_section_id}
                required 
                disabled={!!editingItem}
                placeholder="e.g., A101, FR01" 
                maxLength={10}
              />
            </div>
            <div>
              <Label htmlFor="name">Name *</Label>
              <Input 
                name="name" 
                defaultValue={editingItem?.name}
                required 
                placeholder="e.g., Front Row FMCG" 
              />
            </div>
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Input 
              name="description" 
              defaultValue={editingItem?.description}
              placeholder="Long-form or additional information" 
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="section_type">Section Type</Label>
              <Select value={selectedSectionType || undefined} onValueChange={setSelectedSectionType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select Type (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Fast Pick">Fast Pick</SelectItem>
                  <SelectItem value="Bulk">Bulk</SelectItem>
                  <SelectItem value="Heavy">Heavy</SelectItem>
                  <SelectItem value="Small Parts">Small Parts</SelectItem>
                  <SelectItem value="Fragile">Fragile</SelectItem>
                  <SelectItem value="High Value">High Value</SelectItem>
                  <SelectItem value="Cold Storage">Cold Storage</SelectItem>
                  <SelectItem value="Hazmat">Hazmat</SelectItem>
                </SelectContent>
              </Select>
              <input type="hidden" name="section_type" value={selectedSectionType || ''} />
            </div>
            <div>
              <Label htmlFor="picking_sequence">Picking Sequence</Label>
              <Input 
                name="picking_sequence" 
                type="number"
                min="1"
                value={pickingSequence}
                onChange={(e) => setPickingSequence(e.target.value)}
                placeholder="e.g., 1, 10, 100" 
              />
              <p className="text-xs text-gray-500 mt-1">Lower number = picked first</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="status">Status *</Label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus} required>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Blocked">Blocked</SelectItem>
                </SelectContent>
              </Select>
              <input type="hidden" name="status" value={selectedStatus} />
            </div>
            <div>
              <Label htmlFor="special_handling">Special Handling</Label>
              <Input 
                name="special_handling" 
                defaultValue={editingItem?.special_handling}
                placeholder="e.g., Refrigerated, High Value" 
              />
            </div>
          </div>
        </>
      );
    } else if (activeTab === 'storage-bins') {
      return (
        <>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="storage_bin_id">Storage Bin ID *</Label>
              <Input 
                name="storage_bin_id" 
                defaultValue={editingItem?.storage_bin_id}
                required 
                disabled={!!editingItem}
                placeholder="e.g., 01-A-0001" 
                maxLength={18}
              />
            </div>
            <div>
              <Label htmlFor="name">Name</Label>
              <Input 
                name="name" 
                defaultValue={editingItem?.name}
                placeholder="e.g., Bin A1 Front" 
              />
            </div>
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Input 
              name="description" 
              defaultValue={editingItem?.description}
              placeholder="Notes, instructions, or extended info" 
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="bin_type">Bin Type</Label>
              <Select value={selectedBinType || undefined} onValueChange={setSelectedBinType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select Type (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Standard">Standard</SelectItem>
                  <SelectItem value="Pallet">Pallet</SelectItem>
                  <SelectItem value="Bulk">Bulk</SelectItem>
                  <SelectItem value="PickFace">PickFace</SelectItem>
                  <SelectItem value="Buffer">Buffer</SelectItem>
                  <SelectItem value="Refrigerated">Refrigerated</SelectItem>
                  <SelectItem value="Hazmat">Hazmat</SelectItem>
                  <SelectItem value="High Rack">High Rack</SelectItem>
                </SelectContent>
              </Select>
              <input type="hidden" name="bin_type" value={selectedBinType || ''} />
            </div>
            <div>
              <Label htmlFor="status">Status *</Label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus} required>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Inactive">Inactive</SelectItem>
                  <SelectItem value="Blocked">Blocked</SelectItem>
                </SelectContent>
              </Select>
              <input type="hidden" name="status" value={selectedStatus} />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="capacity_qty">Capacity Quantity</Label>
              <Input 
                name="capacity_qty" 
                type="number"
                min="0"
                step="0.01"
                value={capacityQty}
                onChange={(e) => setCapacityQty(e.target.value)}
                placeholder="e.g., 100" 
              />
            </div>
            <div>
              <Label htmlFor="capacity_unit">Capacity Unit</Label>
              <Input 
                name="capacity_unit" 
                value={capacityUnit}
                onChange={(e) => setCapacityUnit(e.target.value)}
                placeholder="e.g., EA, KG, LTR" 
                maxLength={10}
              />
            </div>
            <div>
              <Label htmlFor="picking_sequence">Picking Sequence</Label>
              <Input 
                name="picking_sequence" 
                type="number"
                min="1"
                value={pickingSequence}
                onChange={(e) => setPickingSequence(e.target.value)}
                placeholder="e.g., 10" 
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="temperature_min">Min Temperature (°C)</Label>
              <Input 
                name="temperature_min" 
                type="number"
                step="0.1"
                value={tempMin}
                onChange={(e) => setTempMin(e.target.value)}
                placeholder="e.g., -18" 
              />
            </div>
            <div>
              <Label htmlFor="temperature_max">Max Temperature (°C)</Label>
              <Input 
                name="temperature_max" 
                type="number"
                step="0.1"
                value={tempMax}
                onChange={(e) => setTempMax(e.target.value)}
                placeholder="e.g., 5" 
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="flex items-center gap-2">
              <input 
                type="checkbox" 
                id="hazardous_allowed"
                name="hazardous_allowed_display"
                checked={hazardousAllowed}
                onChange={(e) => setHazardousAllowed(e.target.checked)}
                className="w-4 h-4"
              />
              <Label htmlFor="hazardous_allowed" className="cursor-pointer">
                Hazardous Allowed
              </Label>
              <input type="hidden" name="hazardous_allowed" value={hazardousAllowed.toString()} />
            </div>

            <div className="flex items-center gap-2">
              <input 
                type="checkbox" 
                id="blocked_for_putaway"
                name="blocked_for_putaway_display"
                checked={blockedPutaway}
                onChange={(e) => setBlockedPutaway(e.target.checked)}
                className="w-4 h-4"
              />
              <Label htmlFor="blocked_for_putaway" className="cursor-pointer">
                Blocked for Putaway
              </Label>
              <input type="hidden" name="blocked_for_putaway" value={blockedPutaway.toString()} />
            </div>

            <div className="flex items-center gap-2">
              <input 
                type="checkbox" 
                id="blocked_for_picking"
                name="blocked_for_picking_display"
                checked={blockedPicking}
                onChange={(e) => setBlockedPicking(e.target.checked)}
                className="w-4 h-4"
              />
              <Label htmlFor="blocked_for_picking" className="cursor-pointer">
                Blocked for Picking
              </Label>
              <input type="hidden" name="blocked_for_picking" value={blockedPicking.toString()} />
            </div>
          </div>
        </>
      );
    }
    return null;
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Extended Warehouse Management (EWM)</h1>
          <p className="text-gray-600 mt-2">Manage warehouses, storage structures, and logistics operations</p>
        </div>

        <div className="flex gap-2 mb-6 border-b border-gray-200">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === tab.id
                  ? 'border-b-2 border-orange-500 text-orange-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <Card className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">{tabs.find(t => t.id === activeTab)?.label}</h2>
            <Dialog 
              key={`${activeTab}-${editingItem ? 'edit' : 'new'}`}
              open={isDialogOpen} 
              onOpenChange={(open) => {
                setIsDialogOpen(open);
                if (!open) {
                  setEditingItem(null);
                  setSelectedCountry('');
                  setSelectedFactoryCalendar('');
                  setSelectedStatus('Active');
                  setSelectedWarehouseType('');
                  setFormResetCounter(prev => prev + 1);
                }
              }}
            >
              <DialogTrigger asChild>
                <Button type="button" data-testid="btn-add-new" disabled={!canWrite(activeTab)} onClick={() => {
                  setEditingItem(null);
                  setSelectedCountry('');
                  setSelectedFactoryCalendar('');
                  setSelectedStatus('Active');
                  setSelectedWarehouseType('');
                  setFormResetCounter(prev => prev + 1);
                }}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add New
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[85vh] overflow-y-auto max-w-3xl">
                <DialogHeader>
                  <DialogTitle>
                    {editingItem ? 'Edit' : 'Create'} Warehouse
                  </DialogTitle>
                </DialogHeader>
                <form 
                  key={`form-${activeTab}-${formResetCounter}`}
                  onSubmit={handleSubmit} 
                  className="space-y-4"
                >
                  {getFormFields()}
                  <div className="flex gap-3 pt-4">
                    <Button type="submit" className="flex-1">
                      {editingItem ? 'Update' : 'Create'}
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => {
                        setIsDialogOpen(false);
                        setEditingItem(null);
                        setSelectedCountry('');
                        setSelectedFactoryCalendar('');
                        setSelectedStatus('Active');
                        setSelectedWarehouseType('');
                        setFormResetCounter(prev => prev + 1);
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  {getTableColumns().map((col, idx) => (
                    <th key={idx} className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {getCurrentData().length === 0 ? (
                  <tr>
                    <td colSpan={getTableColumns().length} className="text-center py-8 text-gray-500">
                      No {activeTab === 'warehouses' ? 'warehouses' : 'storage types'} found
                    </td>
                  </tr>
                ) : (
                  getCurrentData().map((item) => {
                    let itemKey;
                    if (activeTab === 'warehouses') {
                      itemKey = `${activeTab}-${item.warehouse_number}`;
                    } else if (activeTab === 'storage-types') {
                      itemKey = `${activeTab}-${item.storage_type_id}`;
                    } else if (activeTab === 'storage-sections') {
                      itemKey = `${activeTab}-${item.storage_section_id}`;
                    } else if (activeTab === 'storage-bins') {
                      itemKey = `${activeTab}-${item.storage_bin_id}`;
                    }
                    return (
                    <tr key={itemKey} className="border-b border-gray-100 hover:bg-gray-50">
                      {getTableRow(item)}
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          <RecordActions
                            doctype={activeTab}
                            idForTestId={itemKey}
                            onView={() => setViewItem(item)}
                            onEdit={() => handleEdit(item)}
                          />
                        </div>
                      </td>
                    </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title={`${tabs.find(t => t.id === activeTab)?.label || 'Record'} Details`}
        record={viewItem}
      />
    </Layout>
  );
};

export default EWM;
